package de.xtl.retroguide.question

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import de.xtl.retroguide.protocol.AnswerCategory
import de.xtl.retroguide.protocol.MAX_ROTI
import de.xtl.retroguide.protocol.MIN_ROTI
import de.xtl.retroguide.protocol.toRotiScore
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.*

internal val rotiEmojis = listOf("😩", "😕", "😐", "🙂", "🤩")

private val rotiLabels = listOf(Res.string.roti_1, Res.string.roti_2, Res.string.roti_3, Res.string.roti_4, Res.string.roti_5)

@Composable
internal fun rotiLabel(score: Int) = stringResource(rotiLabels[score - MIN_ROTI])

private val AnswerCategory.labelRes: StringResource
    get() = when (this) {
        AnswerCategory.WIND -> Res.string.category_wind
        AnswerCategory.ANCHOR -> Res.string.category_anchor
        AnswerCategory.ROCK -> Res.string.category_rock
        AnswerCategory.ISLAND -> Res.string.category_island
        AnswerCategory.MAD -> Res.string.category_mad
        AnswerCategory.SAD -> Res.string.category_sad
        AnswerCategory.GLAD -> Res.string.category_glad
        AnswerCategory.START -> Res.string.category_start
        AnswerCategory.STOP -> Res.string.category_stop
        AnswerCategory.CONTINUE -> Res.string.category_continue
        AnswerCategory.MORE -> Res.string.category_more
        AnswerCategory.LESS -> Res.string.category_less
        AnswerCategory.LIKED -> Res.string.category_liked
        AnswerCategory.LEARNED -> Res.string.category_learned
        AnswerCategory.LACKED -> Res.string.category_lacked
        AnswerCategory.LONGED_FOR -> Res.string.category_longed_for
        AnswerCategory.FIRE -> Res.string.category_fire
        AnswerCategory.SANDBAG -> Res.string.category_sandbag
        AnswerCategory.SUN -> Res.string.category_sun
        AnswerCategory.STORM -> Res.string.category_storm
        AnswerCategory.ENGINE -> Res.string.category_engine
        AnswerCategory.PARACHUTE -> Res.string.category_parachute
        AnswerCategory.ABYSS -> Res.string.category_abyss
        AnswerCategory.BRIDGE -> Res.string.category_bridge
        AnswerCategory.KEEP -> Res.string.category_keep
        AnswerCategory.ADD -> Res.string.category_add
        AnswerCategory.DROP -> Res.string.category_drop
        AnswerCategory.IMPROVE -> Res.string.category_improve
    }

private val AnswerCategory.hintRes: StringResource
    get() = when (this) {
        AnswerCategory.WIND -> Res.string.category_wind_hint
        AnswerCategory.ANCHOR -> Res.string.category_anchor_hint
        AnswerCategory.ROCK -> Res.string.category_rock_hint
        AnswerCategory.ISLAND -> Res.string.category_island_hint
        AnswerCategory.MAD -> Res.string.category_mad_hint
        AnswerCategory.SAD -> Res.string.category_sad_hint
        AnswerCategory.GLAD -> Res.string.category_glad_hint
        AnswerCategory.START -> Res.string.category_start_hint
        AnswerCategory.STOP -> Res.string.category_stop_hint
        AnswerCategory.CONTINUE -> Res.string.category_continue_hint
        AnswerCategory.MORE -> Res.string.category_more_hint
        AnswerCategory.LESS -> Res.string.category_less_hint
        AnswerCategory.LIKED -> Res.string.category_liked_hint
        AnswerCategory.LEARNED -> Res.string.category_learned_hint
        AnswerCategory.LACKED -> Res.string.category_lacked_hint
        AnswerCategory.LONGED_FOR -> Res.string.category_longed_for_hint
        AnswerCategory.FIRE -> Res.string.category_fire_hint
        AnswerCategory.SANDBAG -> Res.string.category_sandbag_hint
        AnswerCategory.SUN -> Res.string.category_sun_hint
        AnswerCategory.STORM -> Res.string.category_storm_hint
        AnswerCategory.ENGINE -> Res.string.category_engine_hint
        AnswerCategory.PARACHUTE -> Res.string.category_parachute_hint
        AnswerCategory.ABYSS -> Res.string.category_abyss_hint
        AnswerCategory.BRIDGE -> Res.string.category_bridge_hint
        AnswerCategory.KEEP -> Res.string.category_keep_hint
        AnswerCategory.ADD -> Res.string.category_add_hint
        AnswerCategory.DROP -> Res.string.category_drop_hint
        AnswerCategory.IMPROVE -> Res.string.category_improve_hint
    }

@Composable
internal fun AnswerCategory.label() = stringResource(labelRes)

internal val AnswerCategory.accentColor: Color
    get() = when (this) {
        AnswerCategory.WIND -> Color(0xFF0288D1)
        AnswerCategory.ANCHOR -> Color(0xFF5D4037)
        AnswerCategory.ROCK -> Color(0xFF546E7A)
        AnswerCategory.ISLAND -> Color(0xFF2E7D32)
        AnswerCategory.MAD -> Color(0xFFD32F2F)
        AnswerCategory.SAD -> Color(0xFF3949AB)
        AnswerCategory.GLAD -> Color(0xFFF9A825)
        AnswerCategory.START -> Color(0xFF2E7D32)
        AnswerCategory.STOP -> Color(0xFFC62828)
        AnswerCategory.CONTINUE -> Color(0xFF1565C0)
        AnswerCategory.MORE -> Color(0xFF00897B)
        AnswerCategory.LESS -> Color(0xFFEF6C00)
        AnswerCategory.LIKED -> Color(0xFF43A047)
        AnswerCategory.LEARNED -> Color(0xFFFFB300)
        AnswerCategory.LACKED -> Color(0xFF6D4C41)
        AnswerCategory.LONGED_FOR -> Color(0xFF8E24AA)
        AnswerCategory.FIRE -> Color(0xFFE65100)
        AnswerCategory.SANDBAG -> Color(0xFF795548)
        AnswerCategory.SUN -> Color(0xFFF9A825)
        AnswerCategory.STORM -> Color(0xFF455A64)
        AnswerCategory.ENGINE -> Color(0xFF1565C0)
        AnswerCategory.PARACHUTE -> Color(0xFFAD1457)
        AnswerCategory.ABYSS -> Color(0xFF37474F)
        AnswerCategory.BRIDGE -> Color(0xFF2E7D32)
        AnswerCategory.KEEP -> Color(0xFF1565C0)
        AnswerCategory.ADD -> Color(0xFF2E7D32)
        AnswerCategory.DROP -> Color(0xFFC62828)
        AnswerCategory.IMPROVE -> Color(0xFFEF6C00)
    }

@Composable
internal fun CategoryBadge(category: AnswerCategory, modifier: Modifier = Modifier) {
    Surface(
        shape = RoundedCornerShape(50),
        color = category.accentColor.copy(alpha = .14f),
        contentColor = category.accentColor,
        modifier = modifier,
    ) {
        Text(
            "${category.emoji} ${category.label()}",
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
            style = MaterialTheme.typography.labelLarge,
        )
    }
}

@Composable
internal fun CategoryAnswerPicker(
    categories: List<AnswerCategory>,
    selected: AnswerCategory?,
    text: String,
    editing: Boolean,
    enabled: Boolean,
    onCategoryChange: (AnswerCategory) -> Unit,
    onTextChange: (String) -> Unit,
) {
    Text(stringResource(Res.string.category_answer_prompt), style = MaterialTheme.typography.titleMedium)
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        categories.forEach { category ->
            FilterChip(
                selected = selected == category,
                onClick = { onCategoryChange(category) },
                enabled = enabled,
                label = { Text("${category.emoji} ${category.label()}") },
            )
        }
    }
    selected?.let {
        Text(
            stringResource(it.hintRes),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    OutlinedTextField(
        value = text,
        onValueChange = onTextChange,
        label = { Text(stringResource(if (editing) Res.string.answer_edit_label else Res.string.answer_label)) },
        placeholder = { Text(stringResource(Res.string.answer_placeholder)) },
        enabled = enabled,
        minLines = 3,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
internal fun RotiAnswerPicker(
    value: String,
    enabled: Boolean,
    onValueChange: (String) -> Unit,
) {
    Text(stringResource(Res.string.roti_answer_prompt), style = MaterialTheme.typography.titleMedium)
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        (MIN_ROTI..MAX_ROTI).forEach { score ->
            val selected = value == score.toString()
            val scale by animateFloatAsState(
                targetValue = if (selected) 1.1f else 1f,
                animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy),
            )
            val description = "$score – ${rotiLabel(score)}"
            Surface(
                onClick = { onValueChange(score.toString()) },
                enabled = enabled,
                shape = RoundedCornerShape(16.dp),
                color = if (selected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant,
                border = if (selected) BorderStroke(2.dp, MaterialTheme.colorScheme.primary) else null,
                modifier = Modifier
                    .weight(1f)
                    .graphicsLayer {
                        scaleX = scale
                        scaleY = scale
                    }
                    .semantics { contentDescription = description },
            ) {
                Column(
                    modifier = Modifier.padding(vertical = 10.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Text(rotiEmojis[score - MIN_ROTI], fontSize = 28.sp)
                    Text(score.toString(), style = MaterialTheme.typography.titleMedium)
                }
            }
        }
    }
    value.toRotiScore()?.let {
        Text(rotiLabel(it), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.primary)
    }
}

@Composable
internal fun RotiAnswerContent(score: Int) {
    Text(
        "${rotiEmojis[score - MIN_ROTI]} $score – ${rotiLabel(score)}",
        style = MaterialTheme.typography.titleLarge,
    )
}
